import { Routes } from '@angular/router';
import { devRoutes } from './dev-routes';
import { authGuard, noAuthGuard } from './core/auth/auth.guard';
import { complexGuard, noComplexGuard } from './core/auth/complex.guard';
import { onboardingRedirectGuard } from './core/auth/onboarding-redirect.guard';
import { partnerTermsGuard } from './core/auth/partner-terms.guard';
import { managementAccessGuard } from './core/auth/management-access.guard';
import { featureFlagGuard } from './core/auth/feature-flag.guard';
import { premiumGuard } from './core/auth/premium.guard';
import { navPrerequisitesGuard } from './core/auth/nav-prerequisites.guard';
import { unsavedThemeGuard } from './features/settings/appearance-page.guard';
import { FeatureFlagCodes } from './core/constants/feature-flags';
import {
  tournamentDetailRedirect,
  tournamentEditRedirect,
  tournamentListRedirect,
  tournamentNewRedirect,
} from './core/constants/tournament-redirects';

export const routes: Routes = [
  {
    path: 'login',
    canActivate: [noAuthGuard],
    loadComponent: () => import('./features/login/login.component').then(m => m.LoginComponent)
  },
  {
    path: 'forgot-password',
    canActivate: [noAuthGuard],
    loadComponent: () => import('./features/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent)
  },
  // Developer-only routes. Empty in a release build — see dev-routes.ts.
  ...devRoutes,
  {
    path: 'activate/:token',
    loadChildren: () => import('./features/activation/activation.module').then(m => m.ActivationModule)
  },
  {
    // Outside the dashboard shell on purpose: this is where `partnerTermsGuard` sends an admin it
    // has turned away, so it must not itself sit behind that guard, and there must be no
    // navigation on it to click past the decision.
    path: 'partner-terms',
    canActivate: [authGuard, complexGuard],
    loadComponent: () => import('./features/legal/partner-terms.component').then(m => m.PartnerTermsComponent)
  },
  {
    // Same shape as partner-terms: outside the shell, nothing to click past, reachable by the
    // admins the guard turns away.
    path: 'access-closed',
    canActivate: [authGuard, complexGuard],
    loadComponent: () => import('./features/legal/access-closed.component').then(m => m.AccessClosedComponent)
  },
  {
    path: 'create-complex',
    canActivate: [authGuard, noComplexGuard],
    loadComponent: () => import('./features/create-complex/create-complex.component').then(m => m.CreateComplexComponent)
  },
  {
    path: '',
    // Order matters: guards run one after another and the first refusal wins. The closed door
    // is checked before the terms so a locked-out venue is never asked to sign anything. The
    // last one never refuses — it only holds the shell until the menu can be drawn correctly.
    canActivate: [authGuard, complexGuard, managementAccessGuard, partnerTermsGuard, navPrerequisitesGuard],
    loadComponent: () => import('./features/dashboard/dashboard.component').then(m => m.DashboardComponent),
    children: [
      // Not a plain redirectTo: where an admin lands depends on server-side readiness, and
      // redirectTo cannot be async. The guard resolves to /setup or /calendar and never blocks.
      { path: '', pathMatch: 'full', canActivate: [onboardingRedirectGuard], children: [] },
      {
        path: 'setup',
        loadComponent: () => import('./features/onboarding/onboarding-checklist.component').then(m => m.OnboardingChecklistComponent)
      },
      {
        path: 'calendar',
        loadComponent: () => import('./features/calendar/calendar-page.component').then(m => m.CalendarPageComponent)
      },
      // Bookings (pending review) route disabled — booking approval is not enabled; bookings now live in the calendar.
      // {
      //   path: 'bookings',
      //   loadComponent: () => import('./features/bookings/bookings-page.component').then(m => m.BookingsPageComponent)
      // },
      {
        path: 'calendar/import',
        loadChildren: () => import('./features/calendar/import/booking-import.routes').then(m => m.bookingImportRoutes)
      },
      {
        path: 'courts',
        loadComponent: () => import('./features/courts/court-list/court-list.component').then(m => m.CourtListComponent)
      },
      {
        path: 'coaches',
        canActivate: [featureFlagGuard(FeatureFlagCodes.Coaching)],
        loadComponent: () => import('./features/coaches/coach-list/coach-list.component').then(m => m.CoachListComponent)
      },
      {
        path: 'price-rules',
        loadComponent: () => import('./features/price-rules/price-rule-list/price-rule-list.component').then(m => m.PriceRuleListComponent)
      },
      {
        path: 'loyalty',
        loadComponent: () => import('./features/loyalty/loyalty-page.component').then(m => m.LoyaltyPageComponent)
      },
      {
        path: 'price-rules/new',
        loadComponent: () => import('./features/price-rules/price-rule-form/price-rule-form.component').then(m => m.PriceRuleFormComponent)
      },
      {
        path: 'price-rules/:id/edit',
        loadComponent: () => import('./features/price-rules/price-rule-form/price-rule-form.component').then(m => m.PriceRuleFormComponent)
      },
      {
        path: 'working-hours',
        loadComponent: () => import('./features/working-hours/working-hours.component').then(m => m.WorkingHoursComponent)
      },
      {
        path: 'blocked-slots',
        loadComponent: () => import('./features/blocked-slots/blocked-slots-list.component').then(m => m.BlockedSlotsListComponent)
      },
      {
        path: 'amenities',
        loadComponent: () => import('./features/amenities/amenities-page.component').then(m => m.AmenitiesPageComponent)
      },
      // The screen was called Features until amenities got a name of their own. Kept so a
      // bookmark, or a link in an old email, still lands somewhere.
      { path: 'features', redirectTo: 'amenities', pathMatch: 'full' },
      {
        path: 'reviews',
        loadComponent: () => import('./features/reviews/reviews-page.component').then(m => m.ReviewsPageComponent)
      },
      {
        path: 'settings',
        loadComponent: () => import('./features/settings/complex-settings.component').then(m => m.ComplexSettingsComponent)
      },
      {
        path: 'settings/appearance',
        loadComponent: () => import('./features/settings/appearance-page.component').then(m => m.AppearancePageComponent),
        canDeactivate: [unsavedThemeGuard],
      },
      {
        path: 'invoicing',
        loadComponent: () => import('./features/invoicing/invoicing-page.component').then(m => m.InvoicingPageComponent)
      },
      {
        path: 'admins',
        loadComponent: () => import('./features/admin-management/admin-list.component').then(m => m.AdminListComponent)
      },
      {
        path: 'reports',
        loadChildren: () => import('./features/reports/reports.routes').then(m => m.reportsRoutes)
      },
      // Tournaments are events now (`kind = Tournament`), and their screens are gone. The old
      // URLs stay as redirects because venues have them bookmarked and our emails link to them
      // — see `tournament-redirects.ts`. `/tournaments/new` must be declared before
      // `/tournaments/:id`, or "new" would be matched as an id.
      { path: 'tournaments', pathMatch: 'full', redirectTo: tournamentListRedirect },
      { path: 'tournaments/new', pathMatch: 'full', redirectTo: tournamentNewRedirect },
      { path: 'tournaments/:id/edit', pathMatch: 'full', redirectTo: tournamentEditRedirect },
      { path: 'tournaments/:id', pathMatch: 'full', redirectTo: tournamentDetailRedirect },
      // Events ship behind the EVENTS flag: the player app only grows an events surface in a
      // build that is not in the stores yet, so a venue publishing one today would collect
      // registrations nobody can make. The guard sends a venue without the flag to /calendar.
      {
        path: 'events',
        canActivate: [featureFlagGuard(FeatureFlagCodes.Events)],
        loadComponent: () => import('./features/events/event-list/event-list.component').then(m => m.EventListComponent)
      },
      {
        path: 'events/new',
        canActivate: [featureFlagGuard(FeatureFlagCodes.Events)],
        loadComponent: () => import('./features/events/event-form/event-form.component').then(m => m.EventFormComponent)
      },
      {
        path: 'events/:id/edit',
        canActivate: [featureFlagGuard(FeatureFlagCodes.Events)],
        loadComponent: () => import('./features/events/event-form/event-form.component').then(m => m.EventFormComponent)
      },
      {
        path: 'events/:id',
        canActivate: [featureFlagGuard(FeatureFlagCodes.Events)],
        loadComponent: () => import('./features/events/event-detail/event-detail.component').then(m => m.EventDetailComponent)
      },
      {
        path: 'campaigns',
        canActivate: [featureFlagGuard(FeatureFlagCodes.Campaigns)],
        loadComponent: () => import('./features/promotions/promotion-list/promotion-list.component').then(m => m.PromotionListComponent)
      },
      {
        path: 'campaigns/new',
        canActivate: [featureFlagGuard(FeatureFlagCodes.Campaigns)],
        loadComponent: () => import('./features/promotions/promotion-form/promotion-form.component').then(m => m.PromotionFormComponent)
      },
      {
        path: 'campaigns/:id',
        canActivate: [featureFlagGuard(FeatureFlagCodes.Campaigns)],
        loadComponent: () => import('./features/promotions/promotion-detail/promotion-detail.component').then(m => m.PromotionDetailComponent)
      },
      {
        path: 'promotions',
        loadComponent: () => import('./features/promotions/sport-promotions/sport-promotion-list/sport-promotion-list.component').then(m => m.SportPromotionListComponent)
      },
      {
        path: 'promotions/new',
        canActivate: [premiumGuard('/promotions')],
        loadComponent: () => import('./features/promotions/sport-promotions/sport-promotion-form/sport-promotion-form.component').then(m => m.SportPromotionFormComponent)
      },
      {
        path: 'promotions/:id/edit',
        canActivate: [premiumGuard('/promotions')],
        loadComponent: () => import('./features/promotions/sport-promotions/sport-promotion-form/sport-promotion-form.component').then(m => m.SportPromotionFormComponent)
      },
      {
        path: 'discount-codes',
        canActivate: [featureFlagGuard(FeatureFlagCodes.DiscountCodes)],
        loadComponent: () => import('./features/discount-codes/discount-code-list/discount-code-list.component').then(m => m.DiscountCodeListComponent)
      },
      {
        path: 'discount-codes/new',
        canActivate: [featureFlagGuard(FeatureFlagCodes.DiscountCodes), premiumGuard('/discount-codes')],
        loadComponent: () => import('./features/discount-codes/discount-code-form/discount-code-form.component').then(m => m.DiscountCodeFormComponent)
      },
      {
        path: 'discount-codes/:id/edit',
        canActivate: [featureFlagGuard(FeatureFlagCodes.DiscountCodes), premiumGuard('/discount-codes')],
        loadComponent: () => import('./features/discount-codes/discount-code-form/discount-code-form.component').then(m => m.DiscountCodeFormComponent)
      },
      {
        path: 'referrals',
        loadComponent: () => import('./features/referrals/referrals-page.component').then(m => m.ReferralsPageComponent)
      },
      {
        path: 'billing',
        loadChildren: () => import('./features/billing/billing.module').then(m => m.BillingModule)
      },
      {
        path: 'subscription/invoices',
        loadComponent: () => import('./features/billing/subscription-invoices-page.component').then(m => m.SubscriptionInvoicesPageComponent)
      }
    ]
  }
];
